/**
 * Free-form tag editor (Track A · P0.6). A controlled input that renders
 * the current tags as removable chips and appends a new one on Enter or
 * comma. De-duplication + blank-skipping live in `src/lib/tags.ts` so the
 * widget stays presentational. An optional `suggestions` list powers a
 * lightweight datalist-style typeahead (used by the register forms to
 * surface tags already in use elsewhere).
 *
 * Typing `@` opens a roster mention picker (same chrome as OwnerCombobox).
 * Picking a user commits `@Display Name` as a normal tag chip.
 *
 * Styling follows the feature-component convention in this codebase
 * (inline `var(--*)` tokens, `FIELD_CLASS` for the text field) so the chips
 * read correctly in both dark and light themes.
 */

import {
  useEffect,
  useId,
  useMemo,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { X } from "lucide-react";
import { FIELD_CLASS } from "@/features/workshop/modals/log-controls.utils";
import { useAuth } from "@/hooks/use-auth";
import {
  addTag,
  removeTag,
  resolveTagAccentKey,
  resolveTagChipStyle,
} from "@/lib/tags";
import { cn } from "@/lib/utils";

type TagInputProps = {
  value: string[];
  onChange: (next: string[]) => void;
  /** Tags already in use elsewhere, offered as a typeahead. */
  suggestions?: readonly string[];
  /**
   * Roster display names for `@`-mention. When omitted, falls back to
   * `useAuth().users` (deduped + sorted). Tests pass an explicit list.
   */
  mentionOptions?: readonly string[];
  /**
   * Ambient workshop context for free-form tags. Literal `WS1`…`WS8`
   * chips always use that workshop's own workstream accent.
   */
  workshopId?: string;
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
};

const CHIP: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 6px 2px 8px",
  borderRadius: 999,
  background: "rgba(var(--accent-true-blue-rgb), 0.10)",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.28)",
  color: "var(--text-secondary)",
  fontSize: "var(--fs-label)",
  lineHeight: 1.4,
  maxWidth: "100%",
};

/** Normalise a picked roster name into an `@Display Name` tag. */
export function mentionTag(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("@") ? trimmed : `@${trimmed}`;
}

export function TagInput({
  value,
  onChange,
  suggestions,
  mentionOptions,
  workshopId,
  ariaLabel = "Add a tag",
  placeholder = "Add tag… or @user",
  className,
}: TagInputProps) {
  const [draft, setDraft] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  const mentionListId = useId();
  const auth = useAuth();

  const roster = useMemo(() => {
    if (mentionOptions) {
      return Array.from(new Set(mentionOptions.filter(Boolean))).sort((a, b) =>
        a.localeCompare(b),
      );
    }
    return Array.from(
      new Set(auth.users.map((u) => u.displayName).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));
  }, [mentionOptions, auth.users]);

  const mentionMode = draft.startsWith("@");
  const mentionQuery = mentionMode ? draft.slice(1).trim().toLowerCase() : "";

  const matches = useMemo(() => {
    if (!mentionMode) return [];
    return mentionQuery
      ? roster.filter((o) => o.toLowerCase().includes(mentionQuery))
      : roster;
  }, [mentionMode, mentionQuery, roster]);

  // Hide when nothing useful to pick — empty roster, no match, or the
  // lone match already equals the typed @tail (same as OwnerCombobox).
  const sole =
    matches.length === 1 &&
    matches[0]!.toLowerCase() === draft.slice(1).trim().toLowerCase();
  const mentionVisible = mentionOpen && mentionMode && matches.length > 0 && !sole;

  useEffect(() => {
    if (!mentionVisible) setActive(-1);
  }, [mentionVisible]);

  function commit(raw: string): void {
    const next = addTag(value, raw);
    if (next !== value) onChange(next);
    setDraft("");
    setMentionOpen(false);
    setActive(-1);
  }

  function chooseMention(name: string): void {
    const tag = mentionTag(name);
    if (tag) commit(tag);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "ArrowDown") {
      if (mentionMode) {
        if (!mentionVisible) {
          if (matches.length > 0) setMentionOpen(true);
          return;
        }
        e.preventDefault();
        setActive((a) => (a >= matches.length - 1 ? 0 : a + 1));
        return;
      }
    } else if (e.key === "ArrowUp") {
      if (mentionVisible) {
        e.preventDefault();
        setActive((a) => (a <= 0 ? matches.length - 1 : a - 1));
        return;
      }
    } else if (e.key === "Escape") {
      if (mentionVisible) {
        e.preventDefault();
        e.stopPropagation();
        setMentionOpen(false);
        setActive(-1);
        return;
      }
    } else if (e.key === "Enter" || e.key === ",") {
      if (e.key === "Enter" && mentionVisible && matches.length > 0) {
        e.preventDefault();
        chooseMention(matches[active >= 0 ? active : 0]!);
        return;
      }
      e.preventDefault();
      if (draft.trim()) commit(draft);
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      // Quick-delete the last chip when the field is empty.
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div
      className={className}
      style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}
    >
      {value.map((tag) => (
        <span
          key={tag}
          className="tag-chip"
          data-tag-accent={resolveTagAccentKey(tag, workshopId)}
          style={{
            ...CHIP,
            ...resolveTagChipStyle(tag, workshopId),
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {tag}
          </span>
          <button
            type="button"
            onClick={() => onChange(removeTag(value, tag))}
            aria-label={`Remove tag: ${tag}`}
            title="Remove tag"
            style={{
              display: "inline-flex",
              border: 0,
              background: "transparent",
              color: "var(--text-muted)",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <X aria-hidden style={{ width: 12, height: 12 }} strokeWidth={2.5} />
          </button>
        </span>
      ))}
      <PopoverPrimitive.Root
        open={mentionVisible}
        onOpenChange={(next) => {
          if (!next) setMentionOpen(false);
        }}
      >
        <PopoverPrimitive.Anchor asChild>
          <input
            type="text"
            role={mentionMode ? "combobox" : undefined}
            aria-label={ariaLabel}
            aria-expanded={mentionMode ? mentionVisible : undefined}
            aria-controls={mentionVisible ? mentionListId : undefined}
            aria-autocomplete={mentionMode ? "list" : undefined}
            aria-activedescendant={
              active >= 0 ? `${mentionListId}-${active}` : undefined
            }
            autoComplete="off"
            list={
              !mentionMode && suggestions && suggestions.length > 0
                ? listId
                : undefined
            }
            value={draft}
            placeholder={placeholder}
            onChange={(e) => {
              const next = e.target.value;
              setDraft(next);
              setActive(-1);
              setMentionOpen(next.startsWith("@"));
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setMentionOpen(false);
              if (draft.trim() && !draft.startsWith("@")) commit(draft);
              // Don't blur-commit a partial @mention — user may be
              // clicking a roster row (mousedown preventDefault handles
              // that); abandoned `@jo` drafts clear on next focus edit.
            }}
            className={FIELD_CLASS}
            style={{ flex: "1 1 120px", minWidth: 100 }}
          />
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={6}
            className="log-pop log-combo-content"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div id={mentionListId} role="listbox" className="log-combo-list">
              {matches.map((name, i) => (
                <button
                  key={name}
                  id={`${mentionListId}-${i}`}
                  type="button"
                  role="option"
                  aria-selected={i === active || undefined}
                  title={name}
                  className={cn("log-combo-item", i === active && "is-active")}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => chooseMention(name)}
                >
                  @{name}
                </button>
              ))}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
      {!mentionMode && suggestions && suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
    </div>
  );
}
